import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Clock, Phone, Star, Award } from 'lucide-react';
import { api } from '@/lib/api';

export default function BrandDetail() {
    const { slug } = useParams();
    const [brand, setBrand] = useState(null);

    useEffect(() => { api.get(`/brands/${slug}`).then(({ data }) => setBrand(data)); }, [slug]);
    if (!brand) return <div className="min-h-screen flex items-center justify-center text-navy">Loading…</div>;

    return (
        <div data-testid="brand-detail-page">
            <section className="relative h-[60vh] -mt-20 pt-20">
                <img src={brand.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 to-black/80" />
                <div className="relative max-w-7xl mx-auto px-6 md:px-12 h-full flex items-end pb-16 text-white">
                    <div className="flex items-end gap-8">
                        <div className="w-32 h-32 bg-white p-4 flex items-center justify-center">
                            <img src={brand.logo_url} alt={brand.name} className="max-w-full max-h-full object-contain" onError={(e) => { e.target.style.display = 'none'; }} />
                        </div>
                        <div>
                            <div className="overline text-gold mb-2">{brand.category}{brand.is_luxury ? ' • Luxury' : ''}</div>
                            <h1 className="font-serif text-5xl md:text-7xl">{brand.name}</h1>
                            <div className="mt-4 flex items-center gap-3 text-sm text-white/80">
                                <Star size={14} className="text-gold" /> {brand.avg_rating} • {brand.earn_rate}x earn rate
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid lg:grid-cols-3 gap-12">
                <div className="lg:col-span-2">
                    <div className="overline mb-4">About the brand</div>
                    <h2 className="font-serif text-4xl text-navy mb-6">{brand.name}</h2>
                    <p className="text-text-secondary leading-relaxed text-lg">{brand.description}</p>

                    {brand.offers?.length > 0 && (
                        <div className="mt-16" data-testid="brand-offers">
                            <div className="overline mb-4">Current Offers</div>
                            <h3 className="font-serif text-3xl text-navy mb-8">Limited time edits</h3>
                            <div className="grid sm:grid-cols-2 gap-6">
                                {brand.offers.map((o) => (
                                    <div key={o.id} className="card-luxury p-6">
                                        <div className="font-serif text-xl text-navy mb-2">{o.title}</div>
                                        <div className="text-sm text-text-secondary line-clamp-3">{o.description}</div>
                                        {o.coupon_code && <div className="mt-4 bg-cream px-3 py-2 inline-block text-xs font-mono text-navy">{o.coupon_code}</div>}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                <aside className="card-luxury p-8 h-fit">
                    <div className="overline mb-4">Available at</div>
                    <div className="space-y-4">
                        {brand.malls.map((m) => (
                            <Link to={`/mall/${m.slug}`} key={m.id} className="block border-b border-border-subtle pb-4 last:border-0">
                                <div className="font-serif text-xl text-navy">{m.name.replace('Inorbit Mall ', '')}</div>
                                <div className="text-xs text-text-tertiary mt-1 flex items-center gap-2"><MapPin size={11} /> {m.city}</div>
                            </Link>
                        ))}
                    </div>
                    <div className="mt-8 pt-6 border-t border-border-subtle">
                        <div className="overline mb-3">Earn Rate</div>
                        <div className="font-serif text-3xl text-gold">{brand.earn_rate}x</div>
                        <div className="text-xs text-text-tertiary mt-1">points on every ₹100 spent</div>
                    </div>
                </aside>
            </section>
        </div>
    );
}
